import { DateTime } from 'luxon'
import { useRouter } from 'next/router'
import type { ReactElement } from 'react'

import { makeServerProps } from '@/server/query'

import SeriesQ from '@/queries/SeriesQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { SQL } from '@/client/query'
import makePage from '@/client/services/pages/makePage'
import { PROGRAM_WORLD_TO_PROGRAM_SLUG } from '@/client/services/programs/constants'
import * as url from '@/client/services/url'
import { hasUserRole } from '@/client/services/user'
import { isProgramWorld } from '@/client/services/worlds'

import SeriesHome from '@/components/Series/SeriesHome/SeriesHome'
import SeriesLayout from '@/components/Series/SeriesLayout/SeriesLayout'
import { SeriesTab } from '@/components/Series/SeriesLayout/types'

function Series(): ReactElement {
  const query = useQuery()
  const user = useUser()
  const router = useRouter()

  const hasAccessCheck = SQL`
    (${SeriesQ.hasResearchGroup('sat-math-foundations')})
    OR ${hasUserRole(user, 'admin:super')}
    OR ${SeriesQ.isHost({ userId: user.id })}
    OR ${SeriesQ.userMatchesSeriesResearchGroup(user)}
  `

  const id = Number.parseInt(`${router.query.id}`, 10)
  const userId = user.id

  const series = query.one(
    SeriesQ.getSeriesForPage({
      id,
      userId,
      customWhere: hasAccessCheck,
      disableBucketCheck: true,
    })
  )

  return (
    <SeriesLayout
      series={series}
      currentTab={SeriesTab.LEARN}
      title={seriesTitle => seriesTitle}
      description={({ tutorDisplayName, title, startDate }) =>
        `${
          tutorDisplayName
        } is hosting a series titled "${title}" on Schoolhouse.world, a free online tutoring platform. Check it out on ${startDate
          .toUTC()
          .toLocaleString(DateTime.DATETIME_FULL)}.`
      }
      headerOptions={{
        heading: seriesTitle => seriesTitle,
        breadcrumbs: ({ world }) =>
          world != null && world.title !== ''
            ? [
                {
                  label: world.title,
                  url: isProgramWorld(world.slug)
                    ? url.getDashboardProgramsUrl({
                        programSlug: PROGRAM_WORLD_TO_PROGRAM_SLUG[world.slug],
                      })
                    : url.getDashboardSessionsUrl({ worldSlug: world.slug }),
                },
              ]
            : [],
      }}
    >
      <SeriesHome series={series} />
    </SeriesLayout>
  )
}

const SeriesPage = makePage(Series, {
  onboardingUsers: 'allowed',
})

export const getServerSideProps = makeServerProps(SeriesPage)
export default SeriesPage
